import React, { FC, memo, useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CategoryBar from '@/components/CategoryBar/CategoryBar';
import styles from './Layout.module.scss';
import PromotionBanner from '@/components/PromotionBanner/PromotionBanner';
import classNames from 'classnames';
import StickyBar from '@/components/StickyBar/StickyBar';
import CookiesBar from '@/components/CookieBar/CookieBar';
import PopupBanner from '@/components/PopupBanner/PopupBanner';
import PromotionBar from '@/components/PromotionBar/PromotionBar';
import { SHARED_BANNER_MODAL } from '@/constants/homePage';
import { LayoutProps } from './Layout.types';
import Image from 'next/image';
import { useMobile } from '@/hooks/use-mobile';
import { Modal } from 'antd';
import { useRouter } from 'next/router';
import { CloseOutlined } from '@ant-design/icons';
import { handleRichText } from '@/utils/handleRichText';
import Link from 'next/link';
import { useWhatsAppConfig } from '@/hooks/use-whatsapp-config';

const Layout: FC<LayoutProps> = memo(props => {
  const {
    headerFooter,
    initialState,
    pathName,
    children,
    promotionBannerData,
    popUpBannerData,
    page,
    homepage
  } = props;
  const body = homepage?.body;
  const language = initialState?.homePage?.language;
  const articlePage = page;
  const router = useRouter();
  const isMobile = useMobile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const showWhatsAppCTA = useWhatsAppConfig();
  const handleOk = () => {
    setIsModalOpen(false);
    router.push({
      pathname: '/zh/blog'
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleWhatsAppGA = (title: any, url: any) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'blog_whatsapp',
      description: title,
      destinationUrl: url,
      pageURL: window.location.href,
      componentName: 'blog_whatsapp'
    });
  };

  const contactPopup = articlePage?.modular_blocks?.filter((obj: { [x: string]: any }) => {
    return obj['contact_module'];
  })?.[0]?.['contact_module']?.['contact_module']?.[0];

  const stickyBarList = articlePage?.modular_blocks?.filter((obj: any) => {
    const keys = Object.keys(obj);
    return (
      keys.includes('promotional_banner') &&
      obj.promotional_banner.banner_reference[0] &&
      obj.promotional_banner.banner_reference[0].type_of_promotion &&
      obj.promotional_banner.banner_reference[0].type_of_promotion[0] &&
      obj.promotional_banner.banner_reference[0].type_of_promotion[0].sticky_bar
    );
  });

  const wikiCategoryStickyBarList = page?.promotional_banner ?? null;

  const getBarKeyIdAndPromotionData = (type: any) => {
    let barUid;
    let promotionData;
    if (Array.isArray(promotionBannerData) && promotionBannerData?.length > 0) {
      const promotionType =
        promotionBannerData?.length &&
        promotionBannerData[0]?.length &&
        promotionBannerData[0]?.type_of_promotion?.length
          ? promotionBannerData[0]?.type_of_promotion[0]
          : {};
      promotionData = promotionBannerData[0];
      if (Object.keys(promotionType).includes('sticky_bar')) {
        barUid = promotionType?.sticky_bar?._metadata?.uid;
      }
    }

    if (pathName === '/blog' && body?.length) {
      body.map((item: any, index: number) => {
        if (
          item['banner'] &&
          item['banner']['banner_reference'][0] &&
          item['banner']['banner_reference'][0]['_content_type_uid'] === SHARED_BANNER_MODAL
        ) {
          const bannerReference = item.banner.banner_reference[0] || {};
          const promotionTypeData = bannerReference.type_of_promotion || [];
          const promotionType = promotionTypeData[0] || {};
          promotionData = bannerReference;
          if (Object.keys(promotionType).includes('sticky_bar')) {
            barUid = promotionType?.sticky_bar?._metadata?.uid;
          }
        }
      });
    }
    if (
      (pathName === '/blog/[category]/[article]' ||
        (pathName === '/blog/guide/[article]' && page?.isIntroductionPage === false)) &&
      stickyBarList &&
      stickyBarList.length
    ) {
      promotionData = stickyBarList[0]?.promotional_banner?.banner_reference[0];
      const promotionType =
        stickyBarList[0]?.promotional_banner?.banner_reference[0]?.type_of_promotion[0];
      barUid = promotionType?.sticky_bar?._metadata?.uid;
    }

    if (
      (pathName === '/blog/guide/[article]' && page?.isIntroductionPage === true) ||
      pathName === '/blog/guide' ||
      pathName === '/blog/[category]'
    ) {
      promotionData = wikiCategoryStickyBarList?.banner_reference[0];
      barUid =
        wikiCategoryStickyBarList?.banner_reference?.length &&
        wikiCategoryStickyBarList?.banner_reference[0]?.type_of_promotion?.length
          ? wikiCategoryStickyBarList?.banner_reference[0]?.type_of_promotion[0]?.sticky_bar
              ?._metadata?.uid
          : '';
    }
    if (type === 'barUid') {
      return barUid;
    } else {
      return promotionData;
    }
  };

  useEffect(() => {
    const layoutDom = document.documentElement;
    const showPopup = () => {
      const targetScroll = (layoutDom.offsetHeight - layoutDom.clientHeight) / 3;
      setShowContactPopup(layoutDom.scrollTop > targetScroll);
    };
    window.addEventListener('scroll', showPopup);
    return () => {
      window.removeEventListener('scroll', showPopup);
    };
  }, []);

  useEffect(() => {
    if (decodeURI(router?.asPath?.replace(/\s/g, '')).includes('#blog-subscription')) {
      const delayTime =
        pathName === '/blog/[category]/[article]' || pathName === '/blog/guide/[article]'
          ? 600
          : 600;
      setTimeout(() => {
        const targetSubscriptionEle = document.querySelector('#blog-subscription');
        window.scrollTo({
          top: (targetSubscriptionEle as any)?.offsetTop,
          behavior: 'smooth'
        });
      }, delayTime);
    }
  }, [router, page, pathName]);

  return (
    <div className={styles.Layout} id={language?.origin === 'zh' ? styles.zh_font : styles.en_font}>
      {popUpBannerData && <PopupBanner popupBanner={popUpBannerData} />}
      <StickyBar stickyBar={headerFooter} />
      {pathName !== '/blog' && <Header language={language} articleData={articlePage} header={headerFooter} />}
      {pathName !== '/blog' && <CategoryBar language={language} headerAndFooterData={headerFooter} />}
      <main className={styles.Main}>{children}</main>
      <CloseOutlined
        className={classNames(
          styles.close_icon,
          isModalOpen ? styles.show : styles.hide,
          isMobile ? styles.mobile : styles.desktop,
        )}
        onClick={handleCancel}
      />
      <div className={styles.Fix_Bottom}>
        <div className={classNames(isMobile ? styles.mobile_block : styles.flex_block)}>
          {(pathName === '/blog/[category]/[article]' || pathName === '/blog/guide/[article]') &&
          contactPopup?.module_title &&
          showWhatsAppCTA &&
          showContactPopup ? (
            <>
              <div
                className={styles.contact_button}
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                <Image src={contactPopup?.icon?.url} width={20} height={20} alt="" />
                <span className={styles.contact_title}>{contactPopup?.module_title}</span>
              </div>
              <Modal
                style={{ textAlign: 'center' }}
                className={isMobile ? styles.mobile_modal : styles.desktop_modal}
                width={598}
                centered
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                maskClosable={false}
                closeIcon={false}
                footer={
                  <Link href={contactPopup?.action_link?.url?.replace(/\/\//g, '/')} className={styles.rich_btn_link}>
                    <button
                      className={styles.rich_btn}
                      onClick={() => {
                        handleWhatsAppGA(contactPopup?.action_link?.title, contactPopup?.action_link?.url);
                      }}
                    >
                      <Image
                        src="/blog/icon/ArrowRight.svg"
                        className={styles.button_icon}
                        alt={''}
                        width={20}
                        height={20}
                      />
                      <Image
                        src="/blog/icon/TurnRight.svg"
                        className={styles.button_icon_hover}
                        alt={''}
                        width={20}
                        height={20}
                      />
                      {contactPopup?.action_link?.title}
                    </button>
                  </Link>
                }
              >
                {contactPopup?.contact_block[0]?.contact_block?.icon?.url && (
                  <Image
                    className={styles.modal_icon}
                    src={contactPopup.contact_block[0].contact_block.icon?.url}
                    width={64}
                    height={64}
                    alt=""
                  />
                )}
                <h4 className={styles.modal_title}>{contactPopup?.contact_block[0]?.contact_block?.title}</h4>
                <div
                  className={styles.modal_description}
                  id={'rich_text_wrapper'}
                  dangerouslySetInnerHTML={{
                    __html: handleRichText(contactPopup?.contact_block[0]?.contact_block?.description),
                  }}
                ></div>
                {isMobile && <div className={styles.line}></div>}
                <div
                  className={styles.modal_content}
                  id={'rich_text_wrapper'}
                  dangerouslySetInnerHTML={{
                    __html: handleRichText(contactPopup?.contact_block[0]?.contact_block?.content),
                  }}
                ></div>
                <div className={styles.modal_disclaimer}>
                  <div
                    className={styles.modal_disclaimer_scroll}
                    id={'rich_text_wrapper'}
                    dangerouslySetInnerHTML={{
                      __html: handleRichText(contactPopup?.contact_block[0]?.contact_block?.disclaimer),
                    }}
                  ></div>
                </div>
              </Modal>
            </>
          ) : (
            <div></div>
          )}
          <CookiesBar cookiesBar={headerFooter} />
        </div>

        <PromotionBar barData={getBarKeyIdAndPromotionData('promotionData')} />
      </div>
      {pathName !== '/blog' && Array.isArray(promotionBannerData) && promotionBannerData.length > 0 ? (
        <div
          className={classNames(
            styles.Bottom_Promotion_Banner,
            pathName !== '/blog' ? styles.promotional_banner_white_bg : '',
          )}
        >
          {promotionBannerData[0].map((item: any, index: number) => {
            const TypePromotion = item.type_of_promotion || [];
            const promotionType = TypePromotion[0] || {};

            if (Object.keys(promotionType).includes('banner')) {
              return <PromotionBanner key={index} bannerData={item} className={'Landing_Banner'} />;
            }
          })}
        </div>
      ) : (
        ''
      )}
      <Footer articleData={articlePage} language={language} footers={headerFooter?.footer} />
    </div>
  );
});

export default Layout;
